import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
    BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { BarChart3, Globe2, Smartphone, Activity, ArrowRight, MapPin, Layers } from "lucide-react";

const COLORS = ["#002FA7", "#0080FF", "#00C49F", "#FFBB28", "#FF8042", "#A66EFE", "#FF4D6D"];

function fmt(n) {
    if (n == null) return "—";
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return String(n);
}

function lastNDays(n) {
    const until = new Date();
    const since = new Date();
    since.setDate(until.getDate() - n);
    return {
        since: since.toISOString().slice(0, 10),
        until: until.toISOString().slice(0, 10),
    };
}

export default function JobAnalytics() {
    const [boards, setBoards] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [scope, setScope] = useState("board"); // "board" | "job"
    const [boardId, setBoardId] = useState("");
    const [jobId, setJobId] = useState("");
    const [{ since, until }, setRange] = useState(lastNDays(30));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [b, j] = await Promise.all([
                    api.get("/analytics/boards"),
                    api.get("/jobs"),
                ]);
                setBoards(b.data || []);
                const jobsArr = Array.isArray(j.data) ? j.data : (j.data?.items || []);
                setJobs(jobsArr.map((x) => ({ id: x.id, title: x.title })));
                if ((b.data || []).length > 0) setBoardId(b.data[0].id);
                else if (jobsArr.length > 0) {
                    setScope("job");
                    setJobId(jobsArr[0].id);
                }
            } catch (err) { toast.error(formatApiError(err)); }
        })();
    }, []);

    const fetchData = async () => {
        const targetUrl = scope === "board"
            ? boardId && `/analytics/board/${boardId}`
            : jobId && `/analytics/job/${jobId}`;
        if (!targetUrl) { setData(null); return; }
        setLoading(true);
        try {
            const { data } = await api.get(targetUrl, { params: { since, until } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
            setData(null);
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [scope, boardId, jobId, since, until]);

    const funnel = data?.funnel || {};
    const conv = data?.conversion || {};

    const funnelArr = useMemo(() => ([
        { stage: "Views",      value: funnel.job_view || 0,      pct: 100 },
        { stage: "Apply click",value: funnel.apply_click || 0,   pct: conv.click_through_pct || 0 },
        { stage: "Submitted",  value: funnel.apply_submit || 0,  pct: funnel.job_view ? Math.round(((funnel.apply_submit || 0) / funnel.job_view) * 1000) / 10 : 0 },
        { stage: "Applied",    value: funnel.apply_success || 0, pct: conv.overall_conversion_pct || 0 },
    ]), [funnel, conv]);

    const heatmapMatrix = useMemo(() => {
        const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
        let max = 0;
        for (const r of data?.heatmap || []) {
            const dow = Math.min(7, Math.max(1, r.dow)) - 1; // 0-Mon … 6-Sun
            const h = Math.min(23, Math.max(0, r.hour));
            grid[dow][h] += r.count;
            if (grid[dow][h] > max) max = grid[dow][h];
        }
        return { grid, max };
    }, [data]);

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4 sm:space-y-6" data-testid="job-analytics-root">
            <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb inline-flex items-center gap-2">
                        <BarChart3 className="w-3.5 h-3.5" /> Job-board analytics
                    </div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">Funnel · Geography · Performance</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-2">Live tracking of views, apply clicks, submissions and successes from every public landing — broken down by city, country, device, source and time.</p>
                </div>
            </div>

            {/* Controls */}
            <div className="border border-border bg-white p-3 sm:p-4 grid gap-3 sm:grid-cols-12">
                <div className="sm:col-span-3">
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Scope</Label>
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger className="rounded-none h-10" data-testid="ja-scope"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="board">Embed board</SelectItem>
                            <SelectItem value="job">Single job</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-5">
                    {scope === "board" ? (
                        <>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Board</Label>
                            <Select value={boardId} onValueChange={setBoardId}>
                                <SelectTrigger className="rounded-none h-10" data-testid="ja-board"><SelectValue placeholder="Select a board" /></SelectTrigger>
                                <SelectContent>
                                    {boards.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No boards yet — create one in /embed-boards</div>}
                                    {boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </>
                    ) : (
                        <>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Job</Label>
                            <Select value={jobId} onValueChange={setJobId}>
                                <SelectTrigger className="rounded-none h-10" data-testid="ja-job"><SelectValue placeholder="Select a job" /></SelectTrigger>
                                <SelectContent>
                                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </>
                    )}
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Since</Label>
                    <Input type="date" value={since} onChange={(e) => setRange((r) => ({ ...r, since: e.target.value }))} className="rounded-none h-10" data-testid="ja-since" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Until</Label>
                    <Input type="date" value={until} onChange={(e) => setRange((r) => ({ ...r, until: e.target.value }))} className="rounded-none h-10" data-testid="ja-until" />
                </div>
                <div className="sm:col-span-12 flex gap-2 flex-wrap">
                    {[7, 30, 90].map((n) => (
                        <Button key={n} variant="outline" size="sm" className="rounded-none" onClick={() => setRange(lastNDays(n))} data-testid={`ja-range-${n}`}>Last {n}d</Button>
                    ))}
                </div>
            </div>

            {loading && <div className="text-center text-xs text-muted-foreground py-12" data-testid="ja-loading">Loading…</div>}
            {!loading && !data && <div className="text-center text-xs text-muted-foreground py-12">Pick a board or a job to see analytics.</div>}

            {!loading && data && (
                <>
                    {/* Funnel KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ja-funnel">
                        {funnelArr.map((s, i) => (
                            <div key={s.stage} className="border border-border bg-white p-4" data-testid={`ja-funnel-${s.stage.toLowerCase().replace(/\W+/g, "-")}`}>
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                                    <span style={{ background: COLORS[i % COLORS.length] }} className="inline-block w-2 h-2" /> {s.stage}
                                </div>
                                <div className="font-display font-black text-2xl sm:text-3xl mt-1">{fmt(s.value)}</div>
                                <div className="text-[11px] font-mono text-foreground/70 mt-0.5">{i === 0 ? "100%" : `${s.pct}% of views`}</div>
                            </div>
                        ))}
                    </div>

                    {/* Trend */}
                    <div className="border border-border bg-white p-3 sm:p-4">
                        <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 inline-flex items-center gap-1.5 mb-2"><Activity className="w-3.5 h-3.5" /> Daily trend</div>
                        <div style={{ width: "100%", height: 260, minHeight: 200 }}>
                            <ResponsiveContainer>
                                <LineChart data={data.trend || []}>
                                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                                    <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="job_view" name="Views" stroke="#002FA7" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="apply_click" name="Apply clicks" stroke="#0080FF" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="apply_submit" name="Submits" stroke="#FFBB28" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="apply_success" name="Applies" stroke="#00C49F" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
                        {/* Top cities */}
                        <div className="border border-border bg-white p-3 sm:p-4">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 inline-flex items-center gap-1.5 mb-2"><MapPin className="w-3.5 h-3.5" /> Top cities</div>
                            <BarList rows={(data.top_cities || []).map((r) => ({ label: r.city || "Unknown", value: r.count }))} testidPrefix="ja-city" />
                        </div>
                        {/* Top countries */}
                        <div className="border border-border bg-white p-3 sm:p-4">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 inline-flex items-center gap-1.5 mb-2"><Globe2 className="w-3.5 h-3.5" /> Top countries</div>
                            <BarList rows={(data.top_countries || []).map((r) => ({ label: r.country || "Unknown", value: r.count }))} testidPrefix="ja-country" />
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
                        {/* Device */}
                        <div className="border border-border bg-white p-3 sm:p-4">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 inline-flex items-center gap-1.5 mb-2"><Smartphone className="w-3.5 h-3.5" /> Device</div>
                            <div style={{ width: "100%", height: 200, minHeight: 180 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={(data.device_breakdown || []).map((r) => ({ name: r.device || "?", value: r.count }))} dataKey="value" nameKey="name" outerRadius={70} innerRadius={32} label={(e) => `${e.name}`}>
                                            {(data.device_breakdown || []).map((r, i) => (<Cell key={r.device} fill={COLORS[i % COLORS.length]} />))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        {/* Top referrers */}
                        <div className="border border-border bg-white p-3 sm:p-4">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 mb-2">Top referrers</div>
                            <BarList rows={(data.top_referrers || []).map((r) => ({ label: shortHost(r.referrer), value: r.count }))} testidPrefix="ja-ref" />
                        </div>
                        {/* UTM */}
                        <div className="border border-border bg-white p-3 sm:p-4">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 mb-2">UTM sources</div>
                            <BarList rows={(data.top_utm_sources || []).map((r) => ({ label: r.utm_source || "—", value: r.count }))} testidPrefix="ja-utm" />
                        </div>
                    </div>

                    {/* Heatmap */}
                    <div className="border border-border bg-white p-3 sm:p-4 overflow-x-auto">
                        <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 mb-2 inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Day × hour heatmap (all events)</div>
                        <HeatmapGrid matrix={heatmapMatrix.grid} max={heatmapMatrix.max} />
                    </div>

                    {/* Per-job leaderboard (only on board scope) */}
                    {scope === "board" && (data.per_job || []).length > 0 && (
                        <div className="border border-border bg-white p-3 sm:p-4 overflow-x-auto">
                            <div className="text-xs uppercase tracking-widest font-mono text-foreground/70 inline-flex items-center gap-1.5 mb-2"><ArrowRight className="w-3.5 h-3.5" /> Job leaderboard</div>
                            <div style={{ width: "100%", height: Math.max(200, 36 * Math.min((data.per_job || []).length, 12)), minHeight: 200 }}>
                                <ResponsiveContainer>
                                    <BarChart data={data.per_job} layout="vertical" margin={{ left: 16, right: 16 }}>
                                        <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                                        <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                                        <YAxis type="category" dataKey="title" width={180} stroke="#334155" fontSize={11} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="job_view" name="Views" fill="#002FA7" />
                                        <Bar dataKey="apply_click" name="Clicks" fill="#0080FF" />
                                        <Bar dataKey="apply_success" name="Applies" fill="#00C49F" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-3 overflow-x-auto" data-testid="ja-leaderboard-table">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-secondary text-foreground/70">
                                        <tr>
                                            <th className="text-left px-2 py-1.5 font-mono uppercase tracking-widest">Job</th>
                                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-widest">Views</th>
                                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-widest">Clicks</th>
                                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-widest">Submits</th>
                                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-widest">Applies</th>
                                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-widest">Conv%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.per_job.map((j) => (
                                            <tr key={j.job_id} className="border-t border-border" data-testid={`ja-job-row-${j.job_id}`}>
                                                <td className="px-2 py-1.5">{j.title || j.job_id}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{fmt(j.job_view)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{fmt(j.apply_click)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{fmt(j.apply_submit)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{fmt(j.apply_success)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{j.conversion_pct}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function shortHost(url) {
    if (!url) return "—";
    try { return new URL(url).hostname.replace(/^www\./, ""); }
    catch { return url.slice(0, 32); }
}

function BarList({ rows, testidPrefix }) {
    const max = Math.max(1, ...rows.map((r) => r.value));
    if (!rows.length) return <div className="text-xs text-muted-foreground py-6 text-center">No data in range</div>;
    return (
        <ul className="space-y-1.5">
            {rows.map((r, i) => (
                <li key={`${r.label}-${i}`} className="grid grid-cols-12 items-center gap-2 text-xs" data-testid={`${testidPrefix}-${i}`}>
                    <span className="col-span-5 truncate" title={r.label}>{r.label}</span>
                    <span className="sr-only">: </span>
                    <div className="col-span-6 h-2 bg-secondary border border-border overflow-hidden" aria-hidden="true">
                        <div className="h-full bg-ikb" style={{ width: `${(r.value / max) * 100}%` }} />
                    </div>
                    <span className="col-span-1 text-right font-mono text-foreground/70">{fmt(r.value)}</span>
                </li>
            ))}
        </ul>
    );
}

function HeatmapGrid({ matrix, max }) {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const intensity = (v) => {
        if (!max || !v) return "rgba(0,47,167,0.04)";
        const a = 0.1 + 0.85 * (v / max);
        return `rgba(0,47,167,${a.toFixed(3)})`;
    };
    return (
        <div className="inline-block min-w-full">
            <div className="flex">
                <div className="w-10" />
                {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="w-5 text-center text-[9px] font-mono text-foreground/50">{h % 6 === 0 ? h : ""}</div>
                ))}
            </div>
            {days.map((day, d) => (
                <div key={day} className="flex items-center">
                    <div className="w-10 text-[10px] font-mono text-foreground/70 pr-1">{day}</div>
                    {matrix[d].map((v, h) => (
                        <div
                            key={h}
                            title={`${day} ${h}:00 — ${v}`}
                            data-testid={`ja-heat-${d}-${h}`}
                            className="w-5 h-5 border border-white"
                            style={{ background: intensity(v) }}
                        />
                    ))}
                </div>
            ))}
            <div className="text-[10px] font-mono text-foreground/60 mt-2">Peak: {max} events</div>
        </div>
    );
}
